// /src/pages/BigEvent/EditEvent.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import "./big-event.css";

/** --------- utils --------- */
const capWords = (s = "") =>
  s
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());

const toSlug = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

const TZ_SHORTLIST = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Phoenix",
  "America/Bogota",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
];

/** ---- page ---- */
export default function EditEvent() {
  const editingEventId =
    typeof window !== "undefined"
      ? sessionStorage.getItem("editingEventId")
      : null;

  const [evt, setEvt] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load event from Firebase
  useEffect(() => {
    const loadEvent = async () => {
      if (!editingEventId) {
        setLoading(false);
        return;
      }

      try {
        const eventRef = doc(db, "big_events", editingEventId);
        const eventSnap = await getDoc(eventRef);

        if (eventSnap.exists()) {
          setEvt({ id: eventSnap.id, ...eventSnap.data() });
        }
      } catch (error) {
        console.error("Error loading event:", error);
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [editingEventId]);

  // local editable model combining all windows
  const [model, setModel] = useState(() => ({
    // Event
    startLocal: "",
    startTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
    endLocal: "",
    endTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
    stopSalesLocal: "",
    stopSalesTZ: Intl.DateTimeFormat().resolvedOptions().timeZone,
    showCountdown: true,
    name: "",
    flyer: "", // dataURL preview
    agenda: [],
    notes: "",
    shortcut: "",
    aboutHtml: "<p>Describe your event...</p>",
    contactEmail: "",
    // Location
    locationName: "",
    venueName: "",
    address: "",
    locationPhone: "",
    locationDetails: "",
    bookRoomUrl: "",
    // Payments
    stripeConnected: false,
    squareConnected: false,
    wbPayEnabled: false, // coming soon (disabled UI)
    // Team Ticketing
    leaders: { MD: [], SMD: [], CEO: [], EVC: [] },
    manualAdd: { level: "MD", name: "" },
    // Ticket Price
    maxTickets: 0,
    perTxnLimit: 1,
    stopAssignmentsAt: "",
    priceBase: 0,
    priceSteps: [], // up to 3 {date:'', price:0}
    // Custom fields
    customFields: [], // {id,label,type,required}
    // Policies
    policies: {
      footerHtml: "<p>Enter event policies shown to buyers.</p>",
      allowTransfers: true,
    },
    // Speakers
    speakers: [], // {id,name,title,headshot}
    // Product Partners
    partners: [], // {id,name,logoUrl}
    // Promo codes
    promos: [], // {id,code,amount,type:'percent'|'flat',limit}
    // Design
    design: {
      template: "A", // A | B
      color: "#caa032",
      eventBanner: "",
      eventLogo: "",
      locationBanner: "",
      contactBanner: "",
      trailerFile: null,
      trailerName: "",
    },
  }));

  // Update model when event is loaded
  useEffect(() => {
    if (!evt) return;

    setModel((m) => ({
      ...m,
      // Event
      startLocal: evt.startISO
        ? new Date(evt.startISO).toISOString().slice(0, 16)
        : "",
      startTZ: evt.timezone || m.startTZ,
      endTZ: evt.timezone || m.endTZ,
      stopSalesTZ: evt.timezone || m.stopSalesTZ,
      name: evt.title || "",
      shortcut: evt.linkSlug || "",
      contactEmail: evt.contactEmail || "",
      // Location
      locationName: evt.locationName || "",
      // Ticket Price
      priceBase: evt.price || 0,
      // Team Ticketing - convert trackBy object to leaders
      // If trackBy exists, we can pre-populate any necessary data
    }));
  }, [evt]);

  useEffect(() => {
    if (!model.shortcut && model.name) {
      setModel((m) => ({ ...m, shortcut: toSlug(m.name) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.name]);

  // persist updates to Firebase
  const persist = async () => {
    if (!evt) return;
    
    try {
      const eventRef = doc(db, "big_events", evt.id);
      
      const updates = {
        title: model.name || evt.title,
        startISO: model.startLocal
          ? new Date(model.startLocal).toISOString()
          : evt.startISO,
        timezone: model.startTZ,
        linkSlug: model.shortcut || evt.linkSlug,
        price: model.priceBase || evt.price,
        contactEmail: model.contactEmail || "",
        locationName: model.locationName || "",
        venueName: model.venueName || "",
        address: model.address || "",
        locationPhone: model.locationPhone || "",
        locationDetails: model.locationDetails || "",
        bookRoomUrl: model.bookRoomUrl || "",
        aboutHtml: model.aboutHtml || "",
        maxTickets: model.maxTickets || 0,
        perTxnLimit: model.perTxnLimit || 1,
        agenda: model.agenda || [],
        notes: model.notes || "",
        speakers: model.speakers || [],
        partners: model.partners || [],
        promos: model.promos || [],
        customFields: model.customFields || [],
        policies: model.policies || {},
        design: model.design || {},
        updatedAt: Timestamp.now(),
      };

      await updateDoc(eventRef, updates);
      
      // Update local state
      setEvt((prev) => ({ ...prev, ...updates }));
      
      alert(`✅ Changes saved!\n\nEvent: ${model.name}\n\nAll changes have been persisted and will be ready for Stripe integration.`);
    } catch (error) {
      console.error("Error saving event:", error);
      alert("Failed to save changes. Please try again.");
    }
  };

  const handleStripeConnect = () => {
    alert(`Stripe Connect\n\nThis will:\n1. Open Stripe OAuth flow\n2. Connect your Stripe account\n3. Enable live payment processing\n4. Configure webhook endpoints\n\nReady for Stripe AI Agent integration!`);
  };

  const handleSquareConnect = () => {
    alert(`Square Connect\n\nThis will:\n1. Open Square OAuth flow\n2. Connect your Square account\n3. Enable payment processing\n4. Sync transaction data`);
  };

  /** ---------- Windows (tabs) ---------- */
  const WINDOWS = [
    "Event",
    "Location",
    "Payments",
    "Team Ticketing",
    "Ticket Price",
    "Custom Fields",
    "Policies",
    "Speakers",
    "Product Partners",
    "Promo Codes",
    "Design Customization",
    "Email Template",
    "Notes",
  ];

  const [win, setWin] = useState(0);

  /** ---------- Reusable UI bits ---------- */
  const Field = ({ label, children, hint }) => (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <div className="muted small">{hint}</div> : null}
    </label>
  );

  const TwoCol = ({ children }) => <div className="grid-2">{children}</div>;

  /** ---------- Handlers ---------- */
  const onNameChange = (v) => {
    const normal = capWords(v);
    setModel((m) => ({ ...m, name: normal }));
  };

  const addAgendaDay = () =>
    setModel((m) => ({
      ...m,
      agenda: [
        ...m.agenda,
        { id: String(Math.random()), date: "", blocks: [] },
      ],
    }));

  const addAgendaBlock = (dayId) =>
    setModel((m) => ({
      ...m,
      agenda: m.agenda.map((d) =>
        d.id === dayId
          ? {
              ...d,
              blocks: [
                ...d.blocks,
                { id: String(Math.random()), time: "", title: "" },
              ],
            }
          : d
      ),
    }));

  const rmAgendaBlock = (dayId, blockId) =>
    setModel((m) => ({
      ...m,
      agenda: m.agenda.map((d) =>
        d.id === dayId
          ? { ...d, blocks: d.blocks.filter((b) => b.id !== blockId) }
          : d
      ),
    }));

  const addPriceStep = () =>
    setModel((m) =>
      m.priceSteps.length >= 3
        ? m
        : { ...m, priceSteps: [...m.priceSteps, { date: "", price: 0 }] }
    );

  const addCustomField = () =>
    setModel((m) => ({
      ...m,
      customFields: [
        ...m.customFields,
        {
          id: String(Math.random()),
          label: "New field",
          type: "text",
          required: false,
        },
      ],
    }));

  const addLeader = () =>
    model.manualAdd.name &&
    setModel((m) => ({
      ...m,
      leaders: {
        ...m.leaders,
        [m.manualAdd.level]: [
          ...m.leaders[m.manualAdd.level],
          { id: String(Math.random()), name: m.manualAdd.name },
        ],
      },
      manualAdd: { ...m.manualAdd, name: "" },
    }));

  const addSpeaker = () =>
    setModel((m) => ({
      ...m,
      speakers: [
        ...m.speakers,
        { id: String(Math.random()), name: "", title: "", headshot: "" },
      ],
    }));

  const addPartner = () =>
    setModel((m) => ({
      ...m,
      partners: [
        ...m.partners,
        { id: String(Math.random()), name: "", logoUrl: "" },
      ],
    }));

  const addPromo = () =>
    setModel((m) => ({
      ...m,
      promos: [
        ...m.promos,
        {
          id: String(Math.random()),
          code: "",
          amount: 0,
          type: "percent",
          limit: 0,
        },
      ],
    }));

  /** ---------- Windows Render ---------- */
  const renderEvent = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <TwoCol>
        <Field label="Event Beginning (local)">
          <div className="with-icon">
            <input
              type="datetime-local"
              className="input"
              value={model.startLocal}
              onChange={(e) =>
                setModel((m) => ({ ...m, startLocal: e.target.value }))
              }
            />
            <select
              className="input"
              value={model.startTZ}
              onChange={(e) =>
                setModel((m) => ({ ...m, startTZ: e.target.value }))
              }
            >
              {TZ_SHORTLIST.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label="Show Countdown Clock">
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={model.showCountdown}
              onChange={(e) =>
                setModel((m) => ({ ...m, showCountdown: e.target.checked }))
              }
            />
            Enabled
          </label>
        </Field>
      </TwoCol>

      <TwoCol>
        <Field label="Event Ending (local)">
          <div className="with-icon">
            <input
              type="datetime-local"
              className="input"
              value={model.endLocal}
              onChange={(e) =>
                setModel((m) => ({ ...m, endLocal: e.target.value }))
              }
            />
            <select
              className="input"
              value={model.endTZ}
              onChange={(e) =>
                setModel((m) => ({ ...m, endTZ: e.target.value }))
              }
            >
              {TZ_SHORTLIST.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label="Stop Selling Tickets At">
          <div className="with-icon">
            <input
              type="datetime-local"
              className="input"
              value={model.stopSalesLocal}
              onChange={(e) =>
                setModel((m) => ({ ...m, stopSalesLocal: e.target.value }))
              }
            />
            <select
              className="input"
              value={model.stopSalesTZ}
              onChange={(e) =>
                setModel((m) => ({ ...m, stopSalesTZ: e.target.value }))
              }
            >
              {TZ_SHORTLIST.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </Field>
      </TwoCol>

      <TwoCol>
        <Field label="Event Name">
          <input
            className="input"
            value={model.name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Ex: Breaking Barriers 2025"
          />
        </Field>
        <Field label="Shortcut">
          <div className="shortcut-wrap">
            <span className="prefix">wealthbuildersevents.com/</span>
            <input
              className="input no-pad"
              value={model.shortcut}
              onChange={(e) =>
                setModel((m) => ({ ...m, shortcut: toSlug(e.target.value) }))
              }
            />
          </div>
        </Field>
      </TwoCol>

      <TwoCol>
        <Field label="Upload Flyer">
          <input
            type="file"
            accept="image/*"
            className="input"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (!f) return;
              const reader = new FileReader();
              reader.onload = () =>
                setModel((m) => ({ ...m, flyer: String(reader.result) }));
              reader.readAsDataURL(f);
            }}
          />
          {model.flyer && (
            <div style={{ marginTop: 8 }}>
              <img
                src={model.flyer}
                alt="flyer preview"
                style={{ maxWidth: "100%", borderRadius: 8 }}
              />
              <div className="muted small">Crop/adjust UI coming soon.</div>
            </div>
          )}
        </Field>
        <Field label="Contact Email">
          <input
            className="input"
            placeholder="support@yourdomain.com"
            value={model.contactEmail}
            onChange={(e) =>
              setModel((m) => ({ ...m, contactEmail: e.target.value }))
            }
          />
        </Field>
      </TwoCol>

      {/* Agenda */}
      <div style={{ marginTop: 10 }}>
        <div className="be-row between">
          <div className="field-label">Agenda</div>
          <button className="btn subtle" onClick={addAgendaDay}>
            + Add Day
          </button>
        </div>
        {model.agenda.length === 0 && (
          <div className="muted small" style={{ paddingTop: 6 }}>
            No days yet.
          </div>
        )}
        {model.agenda.map((d) => (
          <div key={d.id} className="kpi-card" style={{ marginTop: 10 }}>
            <TwoCol>
              <Field label="Date">
                <input
                  type="date"
                  className="input"
                  value={d.date}
                  onChange={(e) =>
                    setModel((m) => ({
                      ...m,
                      agenda: m.agenda.map((x) =>
                        x.id === d.id ? { ...x, date: e.target.value } : x
                      ),
                    }))
                  }
                />
              </Field>
              <div className="field">
                <span className="field-label">Schedule Blocks</span>
                <div className="action-row" style={{ marginBottom: 8 }}>
                  <button
                    className="btn subtle"
                    onClick={() => addAgendaBlock(d.id)}
                  >
                    + Add Block
                  </button>
                </div>
                {d.blocks.length === 0 && (
                  <div className="muted small">No blocks yet.</div>
                )}
                {d.blocks.map((b) => (
                  <div
                    key={b.id}
                    className="be-row"
                    style={{ gap: 8, marginBottom: 6 }}
                  >
                    <input
                      className="input input-compact"
                      placeholder="HH:MM"
                      value={b.time}
                      onChange={(e) =>
                        setModel((m) => ({
                          ...m,
                          agenda: m.agenda.map((x) =>
                            x.id === d.id
                              ? {
                                  ...x,
                                  blocks: x.blocks.map((y) =>
                                    y.id === b.id
                                      ? { ...y, time: e.target.value }
                                      : y
                                  ),
                                }
                              : x
                          ),
                        }))
                      }
                      style={{ width: 120 }}
                    />
                    <input
                      className="input input-compact"
                      placeholder="Title"
                      value={b.title}
                      onChange={(e) =>
                        setModel((m) => ({
                          ...m,
                          agenda: m.agenda.map((x) =>
                            x.id === d.id
                              ? {
                                  ...x,
                                  blocks: x.blocks.map((y) =>
                                    y.id === b.id
                                      ? { ...y, title: e.target.value }
                                      : y
                                  ),
                                }
                              : x
                          ),
                        }))
                      }
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn subtle danger"
                      onClick={() => rmAgendaBlock(d.id, b.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </TwoCol>
          </div>
        ))}
      </div>

      {/* About */}
      <div style={{ marginTop: 12 }}>
        <div className="field">
          <span className="field-label">About (rich text)</span>
          <div
            className="input"
            contentEditable
            suppressContentEditableWarning
            style={{ minHeight: 120, background: "#0f1218" }}
            onInput={(e) =>
              setModel((m) => ({ ...m, aboutHtml: e.currentTarget.innerHTML }))
            }
            dangerouslySetInnerHTML={{ __html: model.aboutHtml }}
          />
          <div className="muted small">
            Basic formatting via browser toolbar (bold/italic/links).
          </div>
        </div>
      </div>

      {/* Email template modal trigger */}
      <div className="be-row" style={{ marginTop: 10 }}>
        <button className="btn subtle" onClick={() => setEmailTplOpen(true)}>
          Show confirmation email template
        </button>
      </div>
    </div>
  );

  const renderLocation = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <TwoCol>
        <Field label="Event Location Name">
          <input
            className="input"
            value={model.locationName}
            onChange={(e) =>
              setModel((m) => ({ ...m, locationName: e.target.value }))
            }
          />
        </Field>
        <Field label="Venue Name">
          <input
            className="input"
            value={model.venueName}
            onChange={(e) =>
              setModel((m) => ({ ...m, venueName: e.target.value }))
            }
          />
        </Field>
      </TwoCol>

      <TwoCol>
        <Field label="Event Address">
          <input
            className="input"
            value={model.address}
            onChange={(e) =>
              setModel((m) => ({ ...m, address: e.target.value }))
            }
          />
        </Field>
        <Field label="Event Location Phone">
          <input
            className="input"
            value={model.locationPhone}
            onChange={(e) =>
              setModel((m) => ({ ...m, locationPhone: e.target.value }))
            }
          />
        </Field>
      </TwoCol>

      <Field label="Additional Event Location Details">
        <textarea
          className="input"
          rows={4}
          value={model.locationDetails}
          onChange={(e) =>
            setModel((m) => ({ ...m, locationDetails: e.target.value }))
          }
        />
      </Field>

      <Field label="Book Room (partner hotel link)">
        <input
          className="input"
          placeholder="https://hotel-partner.example.com/your-block"
          value={model.bookRoomUrl}
          onChange={(e) =>
            setModel((m) => ({ ...m, bookRoomUrl: e.target.value }))
          }
        />
      </Field>
    </div>
  );

  const renderPayments = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <div className="be-row between">
        <div className="field-label">Payment Providers</div>
      </div>
      <div className="kpi-group" style={{ marginTop: 8 }}>
        <div className="kpi-card">
          <div className="kpi-label">Stripe</div>
          <div className="be-row between" style={{ marginTop: 8 }}>
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={model.stripeConnected}
                onChange={(e) =>
                  setModel((m) => ({ ...m, stripeConnected: e.target.checked }))
                }
              />
              Connected
            </label>
            <button className="btn subtle" onClick={handleStripeConnect}>Connect / Manage</button>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Square</div>
          <div className="be-row between" style={{ marginTop: 8 }}>
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={model.squareConnected}
                onChange={(e) =>
                  setModel((m) => ({ ...m, squareConnected: e.target.checked }))
                }
              />
              Connected
            </label>
            <button className="btn subtle" onClick={handleSquareConnect}>Connect / Manage</button>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">WB Pay (coming soon)</div>
          <div className="be-row between" style={{ marginTop: 8 }}>
            <label className="checkbox-inline">
              <input type="checkbox" checked={model.wbPayEnabled} disabled />
              Enabled
            </label>
            <button className="btn subtle" disabled>
              Setup
            </button>
          </div>
        </div>
      </div>

      <div className="edit-placeholder">
        <div className="field-label">Ticket Fee Policy</div>
        <ul className="edit-list muted small">
          <li>Stripe/Square fees apply per transaction (shown at checkout).</li>
          <li>
            CRM fee: <strong>$1</strong> per ticket (for using our systems).
          </li>
          <li>You’ll be able to override or absorb fees in a future update.</li>
        </ul>
      </div>
    </div>
  );

  const renderTeamTicketing = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <div className="be-row between">
        <div className="field-label">Team Ticketing Upline Lists</div>
        <div className="action-row">
          <select
            className="input input-compact"
            value={model.manualAdd.level}
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                manualAdd: { ...m.manualAdd, level: e.target.value },
              }))
            }
          >
            {["MD", "SMD", "CEO", "EVC"].map((lvl) => (
              <option key={lvl} value={lvl}>
                {lvl}
              </option>
            ))}
          </select>
          <input
            className="input input-compact"
            placeholder="Add name manually"
            value={model.manualAdd.name}
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                manualAdd: { ...m.manualAdd, name: e.target.value },
              }))
            }
          />
          <button className="btn subtle" onClick={addLeader}>
            Add
          </button>
        </div>
      </div>

      <div
        className="be-row"
        style={{ marginTop: 12, gap: 12, alignItems: "stretch" }}
      >
        {["MD", "SMD", "CEO", "EVC"].map((lvl) => (
          <div key={lvl} className="kpi-card" style={{ flex: 1 }}>
            <div className="kpi-label">{lvl} List</div>
            <div className="edit-placeholder small" style={{ padding: 8 }}>
              {model.leaders[lvl].length === 0 ? (
                <div className="muted small">No entries.</div>
              ) : (
                <ul className="edit-list" style={{ marginTop: 6 }}>
                  {model.leaders[lvl].map((p) => (
                    <li key={p.id} className="be-row between">
                      <span>{p.name}</span>
                      <button
                        className="btn subtle danger"
                        onClick={() =>
                          setModel((m) => ({
                            ...m,
                            leaders: {
                              ...m.leaders,
                              [lvl]: m.leaders[lvl].filter(
                                (x) => x.id !== p.id
                              ),
                            },
                          }))
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="muted small" style={{ marginTop: 8 }}>
        These lists power the “Select Upline” options during checkout. If
        someone is missing, add them here.
      </div>
    </div>
  );

  const renderTicketPrice = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <TwoCol>
        <Field label="Maximum Tickets">
          <input
            type="number"
            className="input"
            value={model.maxTickets}
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                maxTickets: Number(e.target.value || 0),
              }))
            }
          />
        </Field>
        <Field label="Per Transaction Ticket Limit">
          <select
            className="input"
            value={model.perTxnLimit}
            onChange={(e) =>
              setModel((m) => ({ ...m, perTxnLimit: Number(e.target.value) }))
            }
          >
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
      </TwoCol>

      <TwoCol>
        <Field label="Stop Ticket Assignments At">
          <input
            type="datetime-local"
            className="input"
            value={model.stopAssignmentsAt}
            onChange={(e) =>
              setModel((m) => ({ ...m, stopAssignmentsAt: e.target.value }))
            }
          />
        </Field>
        <Field label="Base Ticket Price">
          <input
            type="number"
            className="input"
            value={model.priceBase}
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                priceBase: Number(e.target.value || 0),
              }))
            }
          />
        </Field>
      </TwoCol>

      <div className="be-row between" style={{ marginTop: 10 }}>
        <div className="field-label">Scheduled Price Increases (max 3)</div>
        <button className="btn subtle" onClick={addPriceStep}>
          + Add Step
        </button>
      </div>

      {model.priceSteps.length === 0 && (
        <div className="muted small" style={{ marginTop: 6 }}>
          None scheduled yet.
        </div>
      )}

      {model.priceSteps.map((s, idx) => (
        <div key={idx} className="be-row" style={{ gap: 8, marginTop: 8 }}>
          <input
            type="datetime-local"
            className="input input-compact"
            value={s.date}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.priceSteps];
                arr[idx] = { ...arr[idx], date: e.target.value };
                return { ...m, priceSteps: arr };
              })
            }
            style={{ width: 220 }}
          />
          <input
            type="number"
            className="input input-compact"
            value={s.price}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.priceSteps];
                arr[idx] = { ...arr[idx], price: Number(e.target.value || 0) };
                return { ...m, priceSteps: arr };
              })
            }
            style={{ width: 140 }}
          />
          <button
            className="btn subtle danger"
            onClick={() =>
              setModel((m) => ({
                ...m,
                priceSteps: m.priceSteps.filter((_, i) => i !== idx),
              }))
            }
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  const renderCustomFields = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <div className="be-row between">
        <div className="field-label">Checkout Custom Fields</div>
        <button className="btn subtle" onClick={addCustomField}>
          + Add Field
        </button>
      </div>

      {model.customFields.length === 0 && (
        <div className="muted small" style={{ marginTop: 6 }}>
          No custom fields yet.
        </div>
      )}

      {model.customFields.map((f, i) => (
        <div key={f.id} className="be-row" style={{ gap: 8, marginTop: 8 }}>
          <input
            className="input input-compact"
            placeholder="Field label"
            value={f.label}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.customFields];
                arr[i] = { ...arr[i], label: e.target.value };
                return { ...m, customFields: arr };
              })
            }
            style={{ width: 260 }}
          />
          <select
            className="input input-compact"
            value={f.type}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.customFields];
                arr[i] = { ...arr[i], type: e.target.value };
                return { ...m, customFields: arr };
              })
            }
            style={{ width: 160 }}
          >
            <option value="text">Text</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="select">Select</option>
            <option value="checkbox">Checkbox</option>
            <option value="textarea">Textarea</option>
          </select>
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={!!f.required}
              onChange={(e) =>
                setModel((m) => {
                  const arr = [...m.customFields];
                  arr[i] = { ...arr[i], required: e.target.checked };
                  return { ...m, customFields: arr };
                })
              }
            />
            Required
          </label>
          <button
            className="btn subtle danger"
            onClick={() =>
              setModel((m) => ({
                ...m,
                customFields: m.customFields.filter((x) => x.id !== f.id),
              }))
            }
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  const renderPolicies = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <TwoCol>
        <Field label="Allow Ticket Transfers">
          <label className="checkbox-inline">
            <input
              type="checkbox"
              checked={model.policies.allowTransfers}
              onChange={(e) =>
                setModel((m) => ({
                  ...m,
                  policies: { ...m.policies, allowTransfers: e.target.checked },
                }))
              }
            />
            Buyers can transfer tickets
          </label>
        </Field>
      </TwoCol>

      <Field label="Policies (shown on page & checkout)">
        <div
          className="input"
          contentEditable
          suppressContentEditableWarning
          style={{ minHeight: 140, background: "#0f1218" }}
          onInput={(e) =>
            setModel((m) => ({
              ...m,
              policies: {
                ...m.policies,
                footerHtml: e.currentTarget.innerHTML,
              },
            }))
          }
          dangerouslySetInnerHTML={{ __html: model.policies.footerHtml }}
        />
      </Field>
    </div>
  );

  const renderSpeakers = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <div className="be-row between">
        <div className="field-label">Speakers</div>
        <button className="btn subtle" onClick={addSpeaker}>
          + Add Speaker
        </button>
      </div>
      {model.speakers.length === 0 && (
        <div className="muted small" style={{ marginTop: 6 }}>
          No speakers yet.
        </div>
      )}
      {model.speakers.map((s, i) => (
        <div key={s.id} className="kpi-card" style={{ marginTop: 10 }}>
          <TwoCol>
            <Field label="Name">
              <input
                className="input"
                value={s.name}
                onChange={(e) =>
                  setModel((m) => {
                    const arr = [...m.speakers];
                    arr[i] = { ...arr[i], name: e.target.value };
                    return { ...m, speakers: arr };
                  })
                }
              />
            </Field>
            <Field label="Title">
              <input
                className="input"
                value={s.title}
                onChange={(e) =>
                  setModel((m) => {
                    const arr = [...m.speakers];
                    arr[i] = { ...arr[i], title: e.target.value };
                    return { ...m, speakers: arr };
                  })
                }
              />
            </Field>
          </TwoCol>
          <Field label="Headshot">
            <input
              type="file"
              accept="image/*"
              className="input"
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (!f) return;
                const reader = new FileReader();
                reader.onload = () =>
                  setModel((m) => {
                    const arr = [...m.speakers];
                    arr[i] = { ...arr[i], headshot: String(reader.result) };
                    return { ...m, speakers: arr };
                  });
                reader.readAsDataURL(f);
              }}
            />
            {s.headshot && (
              <img
                src={s.headshot}
                alt="headshot"
                style={{
                  marginTop: 8,
                  height: 96,
                  width: 96,
                  borderRadius: 12,
                  objectFit: "cover",
                }}
              />
            )}
          </Field>
          <div className="be-row" style={{ justifyContent: "flex-end" }}>
            <button
              className="btn subtle danger"
              onClick={() =>
                setModel((m) => ({
                  ...m,
                  speakers: m.speakers.filter((x) => x.id !== s.id),
                }))
              }
            >
              Remove Speaker
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPartners = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <div className="be-row between">
        <div className="field-label">Product Partners</div>
        <button className="btn subtle" onClick={addPartner}>
          + Add Partner
        </button>
      </div>

      {model.partners.length === 0 && (
        <div className="muted small" style={{ marginTop: 6 }}>
          No partners yet.
        </div>
      )}

      {model.partners.map((p, i) => (
        <div key={p.id} className="be-row" style={{ gap: 8, marginTop: 10 }}>
          <input
            className="input input-compact"
            placeholder="Partner name"
            value={p.name}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.partners];
                arr[i] = { ...arr[i], name: e.target.value };
                return { ...m, partners: arr };
              })
            }
            style={{ width: 260 }}
          />
          <input
            className="input input-compact"
            placeholder="Logo URL (or upload later)"
            value={p.logoUrl}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.partners];
                arr[i] = { ...arr[i], logoUrl: e.target.value };
                return { ...m, partners: arr };
              })
            }
          />
          <button
            className="btn subtle danger"
            onClick={() =>
              setModel((m) => ({
                ...m,
                partners: m.partners.filter((x) => x.id !== p.id),
              }))
            }
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  const renderPromos = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <div className="be-row between">
        <div className="field-label">Promo Codes</div>
        <button className="btn subtle" onClick={addPromo}>
          + Add Promo
        </button>
      </div>
      {model.promos.length === 0 && (
        <div className="muted small" style={{ marginTop: 6 }}>
          No promo codes yet.
        </div>
      )}
      {model.promos.map((p, i) => (
        <div key={p.id} className="be-row" style={{ gap: 8, marginTop: 8 }}>
          <input
            className="input input-compact"
            placeholder="CODE"
            value={p.code}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.promos];
                arr[i] = { ...arr[i], code: e.target.value.toUpperCase() };
                return { ...m, promos: arr };
              })
            }
            style={{ width: 160 }}
          />
          <select
            className="input input-compact"
            value={p.type}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.promos];
                arr[i] = { ...arr[i], type: e.target.value };
                return { ...m, promos: arr };
              })
            }
            style={{ width: 140 }}
          >
            <option value="percent">Percent %</option>
            <option value="flat">Flat $</option>
          </select>
          <input
            type="number"
            className="input input-compact"
            placeholder="Amount"
            value={p.amount}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.promos];
                arr[i] = { ...arr[i], amount: Number(e.target.value || 0) };
                return { ...m, promos: arr };
              })
            }
            style={{ width: 140 }}
          />
          <input
            type="number"
            className="input input-compact"
            placeholder="Usage limit (0 = unlimited)"
            value={p.limit}
            onChange={(e) =>
              setModel((m) => {
                const arr = [...m.promos];
                arr[i] = { ...arr[i], limit: Number(e.target.value || 0) };
                return { ...m, promos: arr };
              })
            }
            style={{ width: 220 }}
          />
          <button
            className="btn subtle danger"
            onClick={() =>
              setModel((m) => ({
                ...m,
                promos: m.promos.filter((x) => x.id !== p.id),
              }))
            }
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  const renderDesign = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <TwoCol>
        <Field label="Template">
          <div className="action-row">
            {["A", "B"].map((t) => (
              <button
                key={t}
                className={
                  "chip " + (model.design.template === t ? "chip-link" : "")
                }
                onClick={() =>
                  setModel((m) => ({
                    ...m,
                    design: { ...m.design, template: t },
                  }))
                }
                type="button"
              >
                Template {t}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Event Color">
          <input
            type="color"
            className="input"
            value={model.design.color}
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                design: { ...m.design, color: e.target.value },
              }))
            }
          />
        </Field>
      </TwoCol>

      <TwoCol>
        <Field label="Event Banner">
          <input
            type="file"
            accept="image/*"
            className="input"
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                design: {
                  ...m.design,
                  eventBanner:
                    (e.target.files &&
                      e.target.files[0] &&
                      e.target.files[0].name) ||
                    "",
                },
              }))
            }
          />
        </Field>
        <Field label="Event Logo">
          <input
            type="file"
            accept="image/*"
            className="input"
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                design: {
                  ...m.design,
                  eventLogo:
                    (e.target.files &&
                      e.target.files[0] &&
                      e.target.files[0].name) ||
                    "",
                },
              }))
            }
          />
        </Field>
      </TwoCol>

      <TwoCol>
        <Field label="Location Banner">
          <input
            type="file"
            accept="image/*"
            className="input"
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                design: {
                  ...m.design,
                  locationBanner:
                    (e.target.files &&
                      e.target.files[0] &&
                      e.target.files[0].name) ||
                    "",
                },
              }))
            }
          />
        </Field>
        <Field label="Contact Banner">
          <input
            type="file"
            accept="image/*"
            className="input"
            onChange={(e) =>
              setModel((m) => ({
                ...m,
                design: {
                  ...m.design,
                  contactBanner:
                    (e.target.files &&
                      e.target.files[0] &&
                      e.target.files[0].name) ||
                    "",
                },
              }))
            }
          />
        </Field>
      </TwoCol>

      <Field label="Trailer (max 50MB)">
        <input
          type="file"
          accept="video/*"
          className="input"
          onChange={(e) =>
            setModel((m) => ({
              ...m,
              design: {
                ...m.design,
                trailerFile: (e.target.files && e.target.files[0]) || null,
                trailerName:
                  (e.target.files &&
                    e.target.files[0] &&
                    e.target.files[0].name) ||
                  "",
              },
            }))
          }
        />
        {model.design.trailerName && (
          <div className="muted small" style={{ marginTop: 6 }}>
            Selected: {model.design.trailerName}
          </div>
        )}
      </Field>
    </div>
  );

  const renderEmailTemplate = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <div className="field-label" style={{ marginBottom: 8 }}>
        Confirmation Email Template
      </div>
      <div className="muted small" style={{ marginBottom: 8 }}>
        Fully editable editor coming soon. For now you can preview and lightly
        edit HTML below.
      </div>
      <div
        className="input"
        contentEditable
        suppressContentEditableWarning
        style={{ minHeight: 220, background: "#0f1218" }}
        dangerouslySetInnerHTML={{
          __html:
            "<h2>Thank you for your purchase!</h2><p>Your tickets for <strong>" +
            (model.name || "Event") +
            "</strong> are confirmed.</p><p>We’ll send more details as we get closer.</p>",
        }}
      />
    </div>
  );

  const renderNotes = () => (
    <div className="be-card" style={{ padding: 14 }}>
      <Field label="Internal Notes (not public)">
        <textarea
          className="input"
          rows={8}
          value={model.notes}
          onChange={(e) => setModel((m) => ({ ...m, notes: e.target.value }))}
        />
      </Field>
    </div>
  );

  /** email template popup from Event window */
  const [emailTplOpen, setEmailTplOpen] = useState(false);

  /** ---------- master render ---------- */
  if (loading) {
    return (
      <div className="be-wrap">
        <div className="loading-center">
          <div className="loading-spinner"></div>
          <p>Loading event...</p>
        </div>
      </div>
    );
  }

  if (!evt) {
    return (
      <div className="be-wrap">
        <div className="be-card" style={{ padding: 20, textAlign: "center" }}>
          <p className="muted">No event selected to edit.</p>
          <button
            className="btn subtle"
            style={{ marginTop: 10 }}
            onClick={() =>
              window.navigateToCRM && window.navigateToCRM("Big Event Builder")
            }
          >
            ← Back to Builder
          </button>
        </div>
      </div>
    );
  }

  const windowRenderers = [
    renderEvent,
    renderLocation,
    renderPayments,
    renderTeamTicketing,
    renderTicketPrice,
    renderCustomFields,
    renderPolicies,
    renderSpeakers,
    renderPartners,
    renderPromos,
    renderDesign,
    renderEmailTemplate,
    renderNotes,
  ];

  return (
    <div className="be-wrap">
      <div className="be-header">
        <div>
          <h2 className="be-title">Edit Event</h2>
          <p className="be-subtitle">
            Editing: <strong>{evt.title}</strong> • ID:{" "}
            <span className="mono">{evt.id}</span>
          </p>
        </div>
        <div className="be-header-right">
          <button
            className="btn subtle"
            onClick={() =>
              window.navigateToCRM && window.navigateToCRM("Big Event Builder")
            }
          >
            ← Back to Builder
          </button>
          <button className="gold-button" onClick={persist}>
            Save Changes
          </button>
        </div>
      </div>

      {/* tab strip */}
      <div className="be-card" style={{ padding: 10 }}>
        <div className="action-row">
          {WINDOWS.map((w, i) => (
            <button
              key={w}
              className={"chip " + (i === win ? "chip-link" : "")}
              onClick={() => setWin(i)}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      {/* active window */}
      {windowRenderers[win]()}

      {/* email template popup (also accessible from Event window button) */}
      {emailTplOpen && (
        <div className="modal-backdrop" onClick={() => setEmailTplOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Confirmation Email Template</h3>
            </div>
            <div className="modal-body">
              {renderEmailTemplate()}
              <div className="modal-foot">
                <button
                  className="btn subtle"
                  onClick={() => setEmailTplOpen(false)}
                >
                  Close
                </button>
                <button
                  className="gold-button"
                  onClick={() => setEmailTplOpen(false)}
                >
                  Save Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
